import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import AssignSpocModal from './AssignSpocModal';
import QuarterlyHighlights from './QuarterlyHighlights';
import './Dashboard.css';

// ─── Shared helpers ──────────────────────────────────────────────────────────

function getScoreClass(val) {
  if (val >= 70) return 'score-active';
  if (val >= 40) return 'score-moderate';
  return 'score-underutilized';
}

function getScoreEmoji(val) {
  if (val >= 70) return '🟢';
  if (val >= 40) return '🟡';
  return '🔴';
}

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric'
  });
}

// ─── Shared helpers ──────────────────────────────────────────────────────────

function AIImprovementPlan({ prefix }) {
  const [plan, setPlan] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get(`/api/mous/${prefix}/improvement-plan`)
      .then(res => {
        if (mounted) {
          setPlan(res.data.plan);
          setLoading(false);
        }
      })
      .catch(err => {
        if (mounted) {
          console.error("Failed to fetch AI improvement plan:", err);
          setPlan("⚠️ Failed to generate AI improvement plan. Please check API keys.");
          setLoading(false);
        }
      });
    return () => { mounted = false; };
  }, [prefix]);

  return (
    <div className="spoc-tip" style={{ display: 'block', padding: '12px', marginTop: '12px', background: '#fef7e0', border: '1px solid #fbebb5', borderRadius: '6px' }}>
      <h4 style={{ margin: '0 0 8px 0', color: '#b06000', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span>💡</span> AI Improvement Plan
      </h4>
      {loading ? (
        <div style={{ fontSize: '13px', color: '#70757a' }}>Analyzing KPIs and generating a custom plan...</div>
      ) : (
        <div className="markdown-content" style={{ fontSize: '13px', color: '#202124' }}>
          <ReactMarkdown>{plan}</ReactMarkdown>
        </div>
      )}
    </div>
  );
}

// ─── SPOC dashboard ──────────────────────────────────────────────────────────


function SpocDashboard({ permissions, onViewMou }) {
  const spocMous = permissions?.spocMous || [];
  const [mouScores, setMouScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSpocData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSpocData = async () => {
    try {
      setLoading(true);
      const results = await Promise.all(
        spocMous.map(async (prefix) => {
          try {
            const [mouRes, effRes, activitiesRes] = await Promise.all([
              api.get(`/api/mou/${prefix}`),
              api.get(`/api/mous/${prefix}/effectiveness`),
              api.get(`/api/mous/${prefix}/activities`),
            ]);
            const mouData = mouRes.data?.json || mouRes.data?.data || {};
            
            let kpiStrings = [];
            const successMetricsText = mouData.success_metrics || '';
            if (successMetricsText) {
              kpiStrings = successMetricsText
                .split(/[,;]|\sand\s/i)
                .map(item => item.trim())
                .filter(item => item.length > 0);
            }
            if (kpiStrings.length === 0) {
              kpiStrings = mouData.kpis || mouData.key_highlights || [];
            }

            // Compute live activity counters from the actual activities list
            const now = new Date();
            const activities = activitiesRes.data || [];
            const nonCancelled = activities.filter(a => a.status !== 'cancelled');
            const liveMetrics = {
              completedActivities: nonCancelled.filter(a => {
                if (!a.date) return false;
                const end = new Date(`${a.date}T${a.endTime || '23:59'}:00`);
                return now > end;
              }).length,
              inProgressActivities: nonCancelled.filter(a => {
                if (!a.date) return false;
                const start = new Date(`${a.date}T${a.startTime || '00:00'}:00`);
                const end = new Date(`${a.date}T${a.endTime || '23:59'}:00`);
                return now >= start && now <= end;
              }).length,
              upcomingActivities: nonCancelled.filter(a => {
                if (!a.date) return false;
                const start = new Date(`${a.date}T${a.startTime || '00:00'}:00`);
                return now < start;
              }).length,
              cancelledActivities: activities.filter(a => a.status === 'cancelled').length,
            };

            return {
              prefix,
              title: mouRes.data?.title || mouData.title || mouData.partner_name || prefix,
              score: effRes.data?.score ?? 0,
              label: effRes.data?.label ?? 'Under-utilized',
              breakdown: effRes.data?.breakdown ?? {},
              kpis: effRes.data?.kpis ?? [],
              kpiStrings,
              metrics: liveMetrics,
              endDate: mouData.mou_end_date || mouData.end_date || null,
            };
          } catch {
            return { prefix, title: prefix, score: 0, label: 'Under-utilized', breakdown: {}, kpis: [], kpiStrings: [], metrics: { completedActivities: 0, inProgressActivities: 0, upcomingActivities: 0, cancelledActivities: 0 }, endDate: null };
          }
        })
      );
      // Sort: under-utilized first so SPOC acts on them
      results.sort((a, b) => a.score - b.score);
      setMouScores(results);
    } catch (err) {
      setError('Failed to load your MoU effectiveness data.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Loading your MoU dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <p>⚠️ {error}</p>
        <button onClick={fetchSpocData} className="btn-retry">Retry</button>
      </div>
    );
  }

  if (spocMous.length === 0) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>📊 My MoU Dashboard</h1>
          <p>No MoUs are assigned to your account yet.</p>
        </div>
      </div>
    );
  }

  const avgScore = Math.round(mouScores.reduce((s, m) => s + m.score, 0) / mouScores.length);
  const underutilized = mouScores.filter(m => m.label === 'Under-utilized');

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>📊 My MoU Dashboard</h1>
        <p>Track and improve the effectiveness of your assigned partnerships</p>
      </div>

      {/* Quick summary row */}
      <div className="stats-grid">
        <div className="stat-card stat-total" id="spoc-stat-total">
          <div className="stat-icon">📋</div>
          <div className="stat-content">
            <h3>{spocMous.length}</h3>
            <p>Assigned MoUs</p>
          </div>
        </div>
        <div className="stat-card stat-active" id="spoc-stat-avg">
          <div className="stat-icon">📈</div>
          <div className="stat-content">
            <h3>{avgScore}%</h3>
            <p>Avg Effectiveness</p>
          </div>
        </div>
        <div className="stat-card stat-expired" id="spoc-stat-underutilized">
          <div className="stat-icon">⚠️</div>
          <div className="stat-content">
            <h3>{underutilized.length}</h3>
            <p>Need Attention</p>
            <span className="stat-sub">under-utilized</span>
          </div>
        </div>
      </div>

      {/* Alert banner for under-utilized MoUs */}
      {underutilized.length > 0 && (
        <div className="spoc-alert-banner" id="spoc-underutilized-alert">
          <span className="alert-icon">🔴</span>
          <span>
            <strong>{underutilized.length} MoU{underutilized.length > 1 ? 's' : ''} are Under-utilized.</strong>{' '}
            Schedule activities, upload photos, and engage participants to improve their scores.
          </span>
        </div>
      )}

      {/* MoU cards */}
      <div className="spoc-mou-grid">
        {mouScores.map((mou) => {
          const daysLeft = mou.endDate
            ? Math.ceil((new Date(mou.endDate) - new Date()) / (1000 * 60 * 60 * 24))
            : null;

          return (
            <div key={mou.prefix} className={`spoc-mou-card ${getScoreClass(mou.score)}-border`} id={`spoc-card-${mou.prefix}`}>
              {/* Header */}
              <div className="spoc-card-header">
                <div className="spoc-card-title-block">
                  <span className="score-emoji">{getScoreEmoji(mou.score)}</span>
                  <div>
                    <h3 className="spoc-card-title">{mou.title}</h3>
                    <span className="spoc-card-prefix">{mou.prefix}</span>
                  </div>
                </div>
                <div className={`spoc-score-badge ${getScoreClass(mou.score)}`}>
                  {mou.score}%
                </div>
              </div>

              {/* Label + expiry */}
              <div className="spoc-card-meta">
                <span className={`status-text-badge ${mou.label?.toLowerCase().replace(' ', '-')}`}>
                  {mou.label}
                </span>
                {daysLeft !== null && (
                  <span className={`days-badge ${daysLeft <= 0 ? 'urgent' : daysLeft <= 30 ? 'warning' : 'ok'}`}>
                    {daysLeft > 0 ? `${daysLeft}d left` : 'Expired'}
                  </span>
                )}
              </div>

              {/* Breakdown mini-bars */}
              <div className="spoc-breakdown">
                {mou.kpis && mou.kpis.length > 0 ? (
                  mou.kpis.map((kpi, index) => {
                    const label = mou.kpiStrings[index] || `KPI ${index + 1}`;
                    const planned = Number(kpi.planned) || 0;
                    const completed = Number(kpi.completed) || 0;
                    const percent = planned > 0 ? Math.min(100, Math.round((completed / planned) * 100)) : 0;
                    
                    return (
                      <div key={index} className="spoc-bar-row">
                        <span className="spoc-bar-label" title={label}>
                          {label.length > 20 ? label.substring(0, 20) + '...' : label}
                        </span>
                        <div className="progress-bar-container" title={`${completed} completed out of ${planned} planned`}>
                          <div className="progress-bar fill-kpis" style={{ width: `${percent}%` }}></div>
                        </div>
                        <span className="spoc-bar-value" title={`${completed} completed out of ${planned} planned`}>
                          {completed}/{planned}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <div className="no-kpis-dash">
                    <span style={{ fontSize: '0.8rem', color: '#64748b' }}>📈 No KPIs defined to track progress.</span>
                  </div>
                )}
              </div>

              {/* Activity metrics */}
              <div className="spoc-metrics-row">
                <span>✅ {mou.metrics.completedActivities ?? 0} done</span>
                <span>🔄 {mou.metrics.inProgressActivities ?? 0} active</span>
                <span>📅 {mou.metrics.upcomingActivities ?? 0} upcoming</span>
                <span>❌ {mou.metrics.cancelledActivities ?? 0} cancelled</span>
              </div>

              {/* AI Improvement Plan for under-utilized */}
              {mou.label === 'Under-utilized' && (
                <AIImprovementPlan prefix={mou.prefix} />
              )}

              {/* Actions */}
              <div className="spoc-card-actions">
                {onViewMou && (
                  <button
                    className="btn-view-mou"
                    onClick={() => onViewMou(mou.prefix)}
                    id={`btn-view-${mou.prefix}`}
                  >
                    👁️ View MoU
                  </button>
                )}
                {onViewMou && (
                  <button
                    className="btn-view-reports"
                    onClick={() => onViewMou(mou.prefix, 'reports')}
                    id={`btn-reports-${mou.prefix}`}
                  >
                    📈 Reports
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Manager dashboard ───────────────────────────────────────────────────────

function ManagerDashboard({ onViewMou, permissions }) {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [expiringMous, setExpiringMous] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Assign SPOC modal state
  const [assignSpocMou, setAssignSpocMou] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, expiringRes, activitiesRes] = await Promise.all([
        api.get('/api/dashboard/stats', { params: { email: user.email } }),
        api.get('/api/dashboard/expiring', { params: { email: user.email, days: 30 } }),
        api.get('/api/dashboard/recent-activities', { params: { email: user.email, limit: 5 } }),
      ]);
      setStats(statsRes.data);
      setExpiringMous(expiringRes.data);
      setRecentActivities(activitiesRes.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getDaysUntilExpiry = (endDate) => {
    if (!endDate) return null;
    return Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <p>⚠️ {error}</p>
        <button onClick={fetchDashboardData} className="btn-retry">Retry</button>
      </div>
    );
  }

  const averageEffectiveness = stats?.averageEffectiveness ?? 0;
  const breakdown = stats?.effectivenessBreakdown ?? { Active: 0, Moderate: 0, 'Under-utilized': 0 };
  const totalMous = stats?.total ?? 0;
  const getPercentage = (value) => totalMous === 0 ? 0 : Math.round((value / totalMous) * 100);

  const filteredMous = (stats?.mouScores || []).filter(mou => {
    const matchesSearch = mou.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          mou.prefix?.toLowerCase().includes(searchQuery.toLowerCase());
    if (statusFilter === 'all') return matchesSearch;
    return matchesSearch && mou.label?.toLowerCase() === statusFilter.toLowerCase();
  });

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>📊 Dashboard</h1>
        <p>Overview of your MoU activities and effectiveness performance</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card stat-total" id="stat-total-card">
          <div className="stat-icon">📋</div>
          <div className="stat-content"><h3>{stats?.total || 0}</h3><p>Total MoUs</p></div>
        </div>
        <div className="stat-card stat-active" id="stat-active-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content"><h3>{stats?.active || 0}</h3><p>Active MoUs</p></div>
        </div>
        <div className="stat-card stat-expired" id="stat-expired-card">
          <div className="stat-icon">⏰</div>
          <div className="stat-content"><h3>{stats?.expired || 0}</h3><p>Expired MoUs</p></div>
        </div>
        <div className="stat-card stat-activities" id="stat-activities-card">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <h3>{stats?.totalActivities || 0}</h3>
            <p>Total Activities</p>
            <span className="stat-sub">+{stats?.monthActivities || 0} this month</span>
          </div>
        </div>
      </div>

      {/* Effectiveness Summary */}
      <div className="effectiveness-summary-section">
        <h2>📈 MoU Effectiveness Performance</h2>
        <div className="effectiveness-grid">
          <div className="effectiveness-card average-score-card" id="dashboard-average-effectiveness-card">
            <h3>Average Effectiveness Score</h3>
            <div className="gauge-wrapper">
              <div className={`circular-progress ${getScoreClass(averageEffectiveness)}`} style={{ '--percent': averageEffectiveness }}>
                <div className="inner-circle">
                  <span className="score-number">{averageEffectiveness}%</span>
                  <span className="score-label">Average</span>
                </div>
              </div>
            </div>
            <p className="effectiveness-note">
              Weighted average across all partnerships based on completion, validity, frequency and engagement.
            </p>
          </div>

          <div className="effectiveness-card distribution-card" id="dashboard-status-distribution-card">
            <h3>Partnership Status Distribution</h3>
            <div className="dist-list">
              {[
                { key: 'Active', label: 'Active (70–100)', cls: 'active-dot', barCls: 'bar-active' },
                { key: 'Moderate', label: 'Moderate (40–69)', cls: 'moderate-dot', barCls: 'bar-moderate' },
                { key: 'Under-utilized', label: 'Under-utilized (0–39)', cls: 'underutilized-dot', barCls: 'bar-underutilized' },
              ].map(({ key, label, cls, barCls }) => (
                <div key={key} className="dist-item">
                  <div className="dist-header">
                    <span className={`dist-label-indicator ${cls}`}></span>
                    <span>{label}</span>
                    <strong>{breakdown[key] || 0} MoUs ({getPercentage(breakdown[key])}%)</strong>
                  </div>
                  <div className="dist-progress">
                    <div className={`dist-bar ${barCls}`} style={{ width: `${getPercentage(breakdown[key])}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="leaderboard-section">
        <div className="leaderboard-header-row">
          <h2>🏆 MoU Performance Leaderboard</h2>
          <div className="leaderboard-filters">
            <input
              type="text"
              placeholder="Search MoU or prefix..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="status-select">
              <option value="all">All Statuses</option>
              <option value="active">Active (70+)</option>
              <option value="moderate">Moderate (40–69)</option>
              <option value="under-utilized">Under-utilized (&lt;40)</option>
            </select>
          </div>
        </div>

        {filteredMous.length > 0 ? (
          <div className="leaderboard-table-container">
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>MoU Title / Prefix</th>
                  <th>Score</th>
                  <th>Status</th>
                  {onViewMou && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                {filteredMous.map((mou, index) => (
                  <tr key={mou.prefix} className="leaderboard-row">
                    <td><span className={`rank-badge rank-${index + 1}`}>{index + 1}</span></td>
                    <td>
                      <div className="mou-cell">
                        <strong className="mou-cell-title">{mou.title}</strong>
                        <span className="mou-cell-prefix">{mou.prefix}</span>
                      </div>
                    </td>
                    <td><span className={`score-badge ${getScoreClass(mou.score)}`}>{mou.score}%</span></td>
                    <td>
                      <span className={`status-text-badge ${mou.label?.toLowerCase().replace(' ', '-')}`}>
                        {mou.label}
                      </span>
                    </td>
                    {onViewMou && (
                      <td className="action-cell">
                        <button onClick={() => onViewMou(mou.prefix)} className="btn-view-mou">
                          👁️ View
                        </button>
                        {permissions?.can?.assignSpoc && permissions.can.assignSpoc(mou.prefix) && (
                          <button
                            className="btn-assign-spoc"
                            onClick={() => setAssignSpocMou(mou)}
                          >
                            👤 SPOCs
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="no-leaderboard-data"><p>No MoUs match the search filters.</p></div>
        )}
      </div>

      {/* Expiring */}
      {expiringMous.length > 0 && (
        <div className="warning-section">
          <h2>⚠️ Expiring Soon (Within 30 Days)</h2>
          <div className="expiring-list">
            {expiringMous.map((mou) => {
              const daysLeft = getDaysUntilExpiry(mou.end_date || mou.mou_end_date);
              return (
                <div key={mou.prefix} className="expiring-item">
                  <div className="expiring-info">
                    <h4>{mou.title || mou.prefix}</h4>
                    <p className="mou-prefix">{mou.prefix}</p>
                  </div>
                  <div className="expiring-date">
                    <span className={`days-badge ${daysLeft <= 7 ? 'urgent' : 'warning'}`}>
                      {daysLeft > 0 ? `${daysLeft} days left` : 'Expired'}
                    </span>
                    <p>Expires {formatDate(mou.end_date || mou.mou_end_date)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Activities */}
      {recentActivities.length === 0 && (
        <div className="empty-activities">
          <div className="empty-icon">📭</div>
          <h3>No Recent Activities</h3>
          <p>Add activities to track collaboration progress</p>
        </div>
      )}

      {assignSpocMou && (
        <AssignSpocModal
          mouPrefix={assignSpocMou.prefix}
          currentSpocs={assignSpocMou.spocs || []}
          onClose={() => setAssignSpocMou(null)}
          onSuccess={(newSpocs) => {
            setAssignSpocMou(null);
            fetchDashboardData();
          }}
        />
      )}
    </div>
  );
}

// ─── Root component ──────────────────────────────────────────────────────────

function Dashboard({ permissions, onViewMou }) {

  
  if (permissions?.role === 'spoc') {
    return (
      <div className="dashboard-root-wrapper">
        <QuarterlyHighlights />
        <SpocDashboard permissions={permissions} onViewMou={onViewMou} />
      </div>
    );
  }

  if (permissions?.role === 'manager') {
    return (
      <div className="dashboard-root-wrapper">
        <QuarterlyHighlights />
        <ManagerDashboard onViewMou={onViewMou} permissions={permissions} />
      </div>
    );
  }

  // Default fallback for students, teachers, and viewers
  return (
    <div className="dashboard-root-wrapper">
      <QuarterlyHighlights />
    </div>
  );
}

export default Dashboard;
